import React from "react";
import { useTheme } from "@mui/material";
import { FiBriefcase, FiCheck, FiChevronLeft, FiChevronRight, FiCpu, FiFileText, FiUserCheck, FiUsers } from "react-icons/fi";
import { StatusBadge } from "../organisation/OrgModal";
import usePublicPlans from "../../hooks/usePublicPlans";
import { pageWindow, planDisplayName, PLAN_STATE, planState, planStatusKey } from "../../utils/subscriptionData";

export const fieldClass =
  "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50";

export const useFieldStyle = () => {
  const theme = useTheme();
  return { borderColor: theme.palette.secondary.v4, color: theme.palette.text.main };
};

// Pill matching the role and status chips used across the organisation screens
export const Pill = ({ tone = "gray", children }) => {
  const tones = {
    gray: "border-gray-300 bg-gray-100 text-gray-700",
    blue: "border-blue-300 bg-blue-100 text-blue-800",
    amber: "border-amber-300 bg-amber-100 text-amber-800",
    violet: "border-violet-300 bg-violet-100 text-violet-800",
    green: "border-green-300 bg-green-100 text-green-800",
    cyan: "border-cyan-300 bg-cyan-100 text-cyan-800",
    stone: "border-stone-400 bg-stone-200 text-stone-800",
    indigo: "border-indigo-300 bg-indigo-100 text-indigo-800",
    orange: "border-orange-300 bg-orange-100 text-orange-800",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap align-middle rounded-full border px-2.5 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

// A grey placeholder block for content that is still loading
export const Skeleton = ({ className = "h-4 w-full" }) => (
  <span aria-hidden="true" className={`block animate-pulse rounded bg-black/10 ${className}`} />
);

export const METRIC_ICONS = { ai_credits: FiCpu, resume_parses: FiFileText, job_slots: FiBriefcase, recruiter_seats: FiUsers, panelist_seats: FiUserCheck };

export const Feature = ({ children }) => (
  <li className="flex items-start gap-2 text-sm">
    <FiCheck className="mt-0.5 shrink-0 text-green-700" aria-hidden="true" />
    <span>{children}</span>
  </li>
);

// Enterprise and other custom-priced plans. The contact address is deployment config, not code.
const SALES_EMAIL = process.env.REACT_APP_SALES_EMAIL || "";

export const ContactSales = ({ planName = "Enterprise" }) =>
  SALES_EMAIL ? (
    <a
      href={`mailto:${SALES_EMAIL}?subject=${encodeURIComponent(`${planName} plan enquiry`)}`}
      className="inline-block rounded border border-accent-blue px-3 py-1.5 text-sm font-medium text-accent-blue hover:bg-accent-blue hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
    >
      Contact sales
    </a>
  ) : (
    <span className="text-sm text-accent-gray-light">Contact our sales team</span>
  );

// A small pager: previous and next arrows around the page numbers, the current page filled in.
export const MiniPager = ({ page, pages, onChange, disabled = false }) => {
  const theme = useTheme();
  const base =
    "flex h-7 min-w-[1.75rem] items-center justify-center rounded-md border px-1.5 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-black disabled:cursor-not-allowed disabled:opacity-40";
  const border = theme.palette.secondary.v4;
  return (
    <nav aria-label="Pagination" className="flex items-center gap-1">
      <button type="button" aria-label="Previous page" className={`${base} bg-white hover:bg-gray-50`} style={{ borderColor: border }} onClick={() => onChange(page - 1)} disabled={disabled || page <= 1}>
        <FiChevronLeft className="h-4 w-4" aria-hidden="true" />
      </button>
      {pageWindow(page, pages).map((item, index) =>
        item === "…" ? (
          <span key={`gap-${index}`} className="px-0.5 text-gray-400" aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            aria-label={`Page ${item}`}
            aria-current={item === page ? "page" : undefined}
            className={`${base} ${item === page ? "border-transparent text-white" : "bg-white hover:bg-gray-50"}`}
            style={item === page ? { backgroundColor: theme.palette.info.main } : { borderColor: border }}
            onClick={() => onChange(item)}
            disabled={disabled}
          >
            {item}
          </button>
        )
      )}
      <button type="button" aria-label="Next page" className={`${base} bg-white hover:bg-gray-50`} style={{ borderColor: border }} onClick={() => onChange(page + 1)} disabled={disabled || page >= pages}>
        <FiChevronRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </nav>
  );
};

// A colour per paid plan, taken from the plan's place in the catalogue (the order GET /plans returns), so the
// first plans get different colours and a plan added later gets the next one. A plan that isn't in the public
// catalogue (an internal or custom one) gets a colour from its id instead. The trial has its own colour.
// No green or teal: they read as "success" and were mistaken for each other.
const PLAN_TONES = ["blue", "violet", "cyan", "indigo", "stone"];
const TRIAL_TONE = "orange";

const planTone = (plan, catalog) => {
  const id = plan?.plan_id || "";
  const index = catalog.findIndex((item) => item.plan_id === id);
  if (index >= 0) return PLAN_TONES[index % PLAN_TONES.length];
  const sum = [...id].reduce((total, char) => total + char.charCodeAt(0), 0);
  return PLAN_TONES[sum % PLAN_TONES.length];
};

// The organisation's current plan status as one badge: "No plan", "Trial", the plan's name while it is running,
// and the status itself (Trial ended, Payment failed, Cancelling...) when something has changed. Never two.
// Running plans are coloured by plan; the status badges keep their own meaning-based colours.
export const PlanBadge = ({ plan }) => {
  const { plans } = usePublicPlans();
  switch (planState(plan)) {
    case PLAN_STATE.LEGACY:
      return <Pill tone="gray">No plan</Pill>;
    case PLAN_STATE.TRIAL:
      return <Pill tone={TRIAL_TONE}>Trial</Pill>;
    case PLAN_STATE.ACTIVE:
      return <Pill tone={planTone(plan, plans)}>{planDisplayName(plan)}</Pill>;
    default:
      return <StatusBadge status={planStatusKey(plan)} />;
  }
};
